import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";
import { LineChart as LineChartIcon, Layers } from "lucide-react";

const PALETTE = ["#0369a1", "#059669", "#e11d48", "#ca8a04", "#7c3aed", "#ea580c", "#0891b2"];

function fmt(v) {
  const n = Number(v || 0);
  return n.toLocaleString("de-CH", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * Merge multiple per-asset timeseries into a single x-axis of sorted dates.
 * For each series we carry the last-known value forward so the chart stays continuous.
 */
function mergeSeries(series) {
  if (!series || series.length === 0) return [];
  const allDates = new Set();
  series.forEach((s) => (s.points || []).forEach((p) => p.date && allDates.add(p.date)));
  const dates = Array.from(allDates).sort();
  const pointerByAsset = new Map(series.map((s) => [s.asset_id, 0]));
  const lastByAsset = new Map(series.map((s) => [s.asset_id, null]));

  return dates.map((date) => {
    const row = { date };
    for (const s of series) {
      let idx = pointerByAsset.get(s.asset_id);
      while (idx < s.points.length && s.points[idx].date <= date) {
        lastByAsset.set(s.asset_id, s.points[idx].bv);
        idx += 1;
      }
      pointerByAsset.set(s.asset_id, idx);
      const last = lastByAsset.get(s.asset_id);
      row[s.asset_id] = last;
    }
    return row;
  });
}

export function MultiAssetTimelineWidget({ companyId }) {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountFilter, setAccountFilter] = useState("__all__");
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!companyId) return;
    let cancel = false;
    (async () => {
      try {
        const r = await apiClient.get(`/companies/${companyId}/accounts`);
        if (!cancel) {
          setAccounts((r.data || []).filter((a) => a.type === "asset" && String(a.code).startsWith("15")));
        }
      } catch {
        /* accounts optional */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = { limit };
        if (accountFilter && accountFilter !== "__all__") params.account_id = accountFilter;
        const r = await apiClient.get(`/companies/${companyId}/assets/multi-timeline`, { params });
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.detail || "Multi-Timeline konnte nicht geladen werden.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [companyId, accountFilter, limit]);

  const mergedData = useMemo(() => mergeSeries(data?.series || []), [data]);
  const series = data?.series || [];

  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="multi-asset-timeline-widget">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center">
            <Layers className="h-4 w-4 text-indigo-700" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
              <LineChartIcon className="h-3.5 w-3.5 text-indigo-700" />
              Top-Anlagen Buchwert-Verlauf
            </h3>
            <p className="text-[11px] text-slate-500">
              {data
                ? `${series.length} von ${data.total_assets_considered} Anlagen · Währung ${data.currency}`
                : "Lade Anlagen ..."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={accountFilter} onValueChange={setAccountFilter}>
            <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="multi-asset-account-filter">
              <SelectValue placeholder="Anlagekonto filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle Anlagekonten</SelectItem>
              {accounts.map((a) => (
                <SelectItem key={a.code} value={a.code}>
                  {a.code} — {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
            <SelectTrigger className="h-8 w-[95px] text-xs" data-testid="multi-asset-limit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[3, 5, 8, 10].map((n) => (
                <SelectItem key={n} value={String(n)}>Top-{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 mb-2">
          {error}
        </div>
      )}

      {loading && <div className="text-xs text-slate-500 py-8 text-center">Lade ...</div>}

      {!loading && series.length === 0 && !error && (
        <div className="text-xs text-slate-500 py-8 text-center italic" data-testid="multi-asset-empty">
          Keine Anlagen für diesen Filter vorhanden.
        </div>
      )}

      {!loading && series.length > 0 && (
        <div className="space-y-3">
          <div style={{ width: "100%", minWidth: 0, height: 240 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200} initialDimension={{ width: 800, height: 240 }}>
              <LineChart data={mergedData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 10, fill: "#475569" }} width={55} tickFormatter={(v) => fmt(v)} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #cbd5e1" }}
                  formatter={(v, key) => {
                    const s = series.find((x) => x.asset_id === key);
                    return [`${fmt(v)} ${data.currency}`, s?.asset_name || key];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 10 }}
                  formatter={(value) => {
                    const s = series.find((x) => x.asset_id === value);
                    return s?.asset_name || value;
                  }}
                />
                {series.map((s, i) => (
                  <Line
                    key={s.asset_id}
                    type="monotone"
                    dataKey={s.asset_id}
                    stroke={PALETTE[i % PALETTE.length]}
                    strokeWidth={1.8}
                    dot={{ r: 2 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
                {/* Disposal-Markers: vertikale Referenzlinie pro abgegangener Anlage */}
                {series
                  .filter((s) => s.status === "disposed" && s.points.length > 0)
                  .map((s, i) => {
                    const last = s.points[s.points.length - 1];
                    return (
                      <ReferenceLine
                        key={`disp-${s.asset_id}`}
                        x={last.date}
                        stroke={PALETTE[series.indexOf(s) % PALETTE.length]}
                        strokeDasharray="4 3"
                        strokeOpacity={0.6}
                        ifOverflow="extendDomain"
                        label={{
                          value: "✕",
                          position: "top",
                          fill: PALETTE[series.indexOf(s) % PALETTE.length],
                          fontSize: 11,
                        }}
                        data-testid={`multi-asset-disposal-marker-${s.asset_id}`}
                      />
                    );
                  })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {series.map((s, i) => (
              <div
                key={s.asset_id}
                className="flex items-center justify-between text-xs rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5"
                data-testid={`multi-asset-legend-${s.asset_id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
                  />
                  <span className="truncate">{s.asset_name}</span>
                  {s.status === "disposed" && (
                    <span className="text-[9px] uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 rounded px-1 py-px">
                      Abg.
                    </span>
                  )}
                </div>
                <div className="font-mono font-semibold text-slate-700 shrink-0">
                  {fmt(s.current_book_value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
